/* eslint-disable max-lines */
import React, { Component } from 'react'
import PropTypes from 'prop-types'

import DateRangePicker from '@wf/react-daterange-picker'

import { isValidDateOrRange, moment } from '../../util/date'
import { toMomentRange } from './moment'
import * as customPropTypes from './prop-types'

const propTypes = {
    className: PropTypes.string,
    disableYearMonthChoiceArrows: PropTypes.bool,
    limitPeriod: PropTypes.number,
    locale: PropTypes.string,
    max: PropTypes.instanceOf(moment),
    min: PropTypes.instanceOf(moment),
    onChange: PropTypes.func,
    rangeSelection: PropTypes.bool,
    value: PropTypes.oneOfType([PropTypes.instanceOf(Date), customPropTypes.dateRange])
}

const defaultProps = {
    locale: 'en-GB',
    onChange: () => {},
    rangeSelection: false,
    value: moment.range()
}

/**
 * The date-picker-pure component. Contains the react-daterange-picker widget without anything else.
 *
 * DatePicker's API (props) to the outside world are based on Date and
 * `{ start: Date, end: Date }` as data types for the `value`. Internally it
 * will always use `moment.range()` for the sake of a consistent data model.
 *
 * Known limitations:
 *   Since there is no `24:00` (esp. in 12-hour-clock), the range's maximum time
 *   in a day is 23:59:59.999. Since the backend treats intervals as `[from, to)`
 *   ("from" inclusive, "to" exclusive) the selected interval will not cover the
 *   day's very last millisecond.
 *
 */
class DatePickerPure extends Component {
    constructor(props) {
        super(props)

        const { locale, rangeSelection, value } = props
        this.state = {
            // Why we store the date in the state:
            // If no default date has been provided for a singe-date selection,
            // use a default date set to today or the minimum date, if provided.
            value: isValidDateOrRange(value) || rangeSelection ? toMomentRange(value, locale) : this.getDefaultValue()
        }

        this.updatePrevented = false
        this.onManualDateRangeChange = this.onManualDateRangeChange.bind(this)
        this.lock = this.lock.bind(this)
        this.unlock = this.unlock.bind(this)
    }

    static getDerivedStateFromProps(props) {
        const { locale, value } = props
        if (value !== undefined) {
            return { value: toMomentRange(value, locale) }
        }
    }

    shouldComponentUpdate() {
        return !this.updatePrevented
    }

    render() {
        const { className, disableYearMonthChoiceArrows, limitPeriod, locale, max, min, rangeSelection } = this.props
        const localeData = moment.localeData(locale) || moment.localeData()
        const roundedValue = this.roundValueToFullDays(this.state.value)
        const value = rangeSelection ? roundedValue : roundedValue.start
        const classNames = 't3sel-date-picker-pure' + ' ' + className

        return (
            <DateRangePicker
                {...{ disableYearMonthChoiceArrows, limitPeriod, locale }}
                className={classNames}
                ref={datePicker => {
                    this.dateRangePicker = datePicker
                }}
                value={isValidDateOrRange(value) ? value : null}
                firstOfWeek={localeData.firstDayOfWeek()}
                minimumDate={min && min.toDate()}
                maximumDate={max && max.toDate()}
                numberOfCalendars={1}
                onSelect={this.onManualDateRangeChange}
                onSelectStart={this.lock}
                selectionType={rangeSelection ? 'range' : 'single'}
                singleDateRange
            />
        )
    }

    onManualDateRangeChange(changeValue) {
        const { locale, onChange, value } = this.props
        const rangeValue = toMomentRange(changeValue, locale)
        this.unlock()
        if (value === undefined) {
            this.setState({ value: rangeValue })
        }
        // work-around for tablet support: for single clicks in Rangeselection.
        const newValue = this.dateRangePicker.finishRangeSelection() || rangeValue
        const roundedValue = this.roundValueToFullDays(newValue)

        if (!isValidDateOrRange(roundedValue) || !roundedValue || !this.checkTimes(roundedValue)) {
            return
        }

        this.setState({ timeFromError: null, timeToError: null })
        onChange(roundedValue)
    }

    /**
     * If rangeSelection then check if start date-time < end date-time.
     * Returns false if the check fails and sets error messages in the state.
     *
     * @param {Moment|MomentRange} roundedValue     Value (selected dates) with times already applied.
     * @return {bool}   true if everything is alright.
     */
    checkTimes(roundedValue) {
        const { rangeSelection } = this.props

        if (!rangeSelection) {
            return true
        }

        if (roundedValue.start.isAfter(roundedValue.end)) {
            this.setState({
                timeFromError: new Error('The start time should be before the end time.')
            })
            return false
        }

        return true
    }

    /**
     * If a moment-range is given it will round the range's start and end to full
     * days, so the react-daterange-picker will display the snake correctly.
     *
     * @param {MomentRange} value
     * @return {MomentRange}
     */
    roundValueToFullDays(value) {
        return moment.range(moment(value.start).startOf('day'), moment(value.end).endOf('day'))
    }

    getDefaultValue() {
        const { locale, max, min, rangeSelection } = this.props
        const today = new Date()
        // If today is within the bounds or if no bounds have been provided, use today.
        // Otherwise, use min or max, whichever is set.
        return toMomentRange(
            (!min || today >= min) && (!max || today <= max) ? today : min || max,
            locale,
            rangeSelection
        )
    }

    lock() {
        this.updatePrevented = true
    }

    unlock() {
        this.updatePrevented = false
    }
}

DatePickerPure.propTypes = propTypes
DatePickerPure.defaultProps = defaultProps

export { DatePickerPure }
